import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@weezevent/nacre';

function Step({ child, index, steps, onChangeSteps }) {
    const { t } = useTranslation();
    // Calculate step number
    const step = index + 1;
    // Construct step title
    const title = `${step} - ${child?.props?.title}`;
    const nextsubtitle = `${child?.props?.inactive}`;
    // Get the state of the current step
    const stepState = Object.values(steps)[index];
    // Determine subtitle based on step state
    // If step is not 'done', use the regular subtitle, otherwise use 'inactive' text
    const subtitle = stepState !== 'done' ? `${child?.props?.subtitle}` : nextsubtitle;

    const handleSetSelf = React.useCallback(() => {
        onChangeSteps(step);
    }, [onChangeSteps, step]);

    return (
        <Card.FormStep
            editLabel={t('ticket.common.action.edit')}
            title={title}
            subtitle={child?.props?.subtitle}
            summary={subtitle}
            onCardEdit={handleSetSelf}
            state={stepState}
            useSteps={true}
        >
            {child}
        </Card.FormStep>
    );
}

export default function Stepper({ children, step, onChangeSteps }) {
    const { t } = useTranslation();

    const steps = React.useMemo(() => {
        let output = {};
        children
            .filter(child => child !== undefined)
            .forEach((child, index) => {
                const stepIndex = index + 1;
                // Determine step state based on current step
                if (step === stepIndex) {
                    output[index] = 'open';
                } else if (stepIndex < step) {
                    output[index] = 'done';
                } else {
                    output[index] = 'closed';
                }
            });

        return output;
    }, [children, step]);

    // Determine whether to move to next or previous step
    const handleChange = React.useCallback(() => {
        if (Object.values(steps)[0] === 'done') {
            onChangeSteps(step);
        } else {
            onChangeSteps(step - 1);
        }
    }, [steps, step, onChangeSteps]);

    return (
        <Card.FormManager
            useSteps
            withStepsMobile
            steps={steps}
            onChangeSteps={handleChange}
            labelPreviousStepMobile={t(['ticket.common.back_step'])}
            labelStepMobile={t(['ticket.common.step.current'])}
        >
            {React.Children.toArray(children).map((child, index) => (
                <Step key={index} child={child} index={index} steps={steps} onChangeSteps={onChangeSteps} />
            ))}
        </Card.FormManager>
    );
}
